import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { StraziService } from '../strazi.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { Observable, finalize, Subject, of, switchMap, distinctUntilChanged, takeUntil, tap, catchError, debounceTime, map } from 'rxjs';
import { helper } from 'src/environments/helper';
import { Generic } from 'src/app/interfaces/generic.interface';
import { JudeteService } from '../../judete/judete.service';
import { StradaUpdate } from '../strazi.interface';
import { notDefaultValidator } from 'src/app/shared/validators/not-default';
import { LocalitatiService } from '../../localitati/localitati.service';

@Component({
    selector: 'app-edit-strazi',
    templateUrl: './edit-strazi.component.html',
    styleUrls: ['./edit-strazi.component.scss']
})
export class EditStraziComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;
    tari: Generic[] = this.data.tari;
    judete: Generic[] = [];
    localitati!: Generic[];

    private editDestroy$ = new Subject<void>();

    // Filtered fields for search
    filteredTari: any[] = this.data.tari;
    tariControl = new FormControl();

    filteredJudete: any[] = [];
    judeteControl = new FormControl();

    localitatiControl = new FormControl();
    isLocalitatiLoading = false;
    showLocalitatiMessage = true;
    localitatiLoadComplete = false;

    form: FormGroup = new FormGroup({
        name: new FormControl(this.data.data.name, Validators.required),
        id_country: new FormControl(this.data.data.country_id, [Validators.required, notDefaultValidator('-1')]),
        id_judet: new FormControl(this.data.data.judet_id, [Validators.required, notDefaultValidator('-1')]),
        id_localitate: new FormControl(this.data.data.localitate_id, [Validators.required, notDefaultValidator('-1')])
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<EditStraziComponent>,
        private straziService: StraziService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private judeteService: JudeteService,
        private localitatiService: LocalitatiService
    ) {
        this.processToken();

        this.form.get('id_country')?.disable();
    }

    ngOnInit() {
        this.renderFieldsSearch();
        console.log(this.data.data);

        if (this.data.data.country_id) {
            this.loadJudete(this.data.data.country_id);
        }

        this.localitatiControl.patchValue(this.data.data.localitate_name);
    }

    ngOnDestroy(): void {
        this.editDestroy$.next();
        this.editDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.localitatiService.setToken(token);
        });
    }

    renderFieldsSearch() {
        this.tariControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredTari = this.tari;
                    return of(this.tari);
                }
                return this.filterTari(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTari = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.judeteControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredJudete = this.judete;
                    return of(this.judete);
                }
                return this.filterJudete(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredJudete = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.localitatiControl.valueChanges
            .pipe(
                takeUntil(this.editDestroy$),
                debounceTime(600)
            )
            .subscribe(localitate => {
                this.fetchLocalitati(localitate);
                this.showLocalitatiMessage = true;
                this.localitatiLoadComplete = false;
            });
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterTari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.tari.filter(tara =>
            this.normalizeString(tara.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onTaraSelectClosed() {
        const selectedValue = this.tariControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTari.some(tara =>
                tara.id === this.form.get('id_country')?.value
            );

            if (!isSelectedInFilteredList) {
                this.tariControl.setValue(null);
                this.filteredTari = this.tari;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onTaraChange(taraId: number): void {
        this.form.controls['id_judet'].patchValue(-1);
        this.form.controls['id_localitate'].patchValue(-1);

        if (taraId == -1) {
            this.judete = [];
            this.filteredJudete = [];

            this.localitati = [];
            return
        }

        if (taraId) {
            this.loadJudete(taraId);
        }
    }

    loadJudete(taraId: number) {
        this.isLoading = true;

        let judeteObservable$ = taraId
            ? this.judeteService.getJudeteActiveByTari(taraId)
            : this.judeteService.getJudeteActive();

        judeteObservable$.pipe(
            map((results: any) => [...results.data]),
            finalize(() => {
                this.isLoading = false;
            })
        ).subscribe({
            next: (judete) => {
                this.judete = [...judete];
                this.filteredJudete = [...judete];
            },
            error: (err) => {
                console.log(err);
                this.filteredJudete = [];
            }
        });
    }

    onJudetChange(judetId: number) {
        this.form.controls['id_localitate'].patchValue(-1);
        this.localitatiControl.patchValue('');
        this.localitati = [];
        this.showLocalitatiMessage = true;
        this.localitatiLoadComplete = false;
    }

    onJudeteSelectClosed() {
        const selectedValue = this.judeteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredJudete.some(judet =>
                judet.id === this.form.get('id_judet')?.value
            );

            if (!isSelectedInFilteredList) {
                this.judeteControl.setValue(null);
                this.filteredJudete = this.judete;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterJudete(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.judete.filter(judet =>
            this.normalizeString(judet.name).includes(normalizedSearchTerm)
        ));
    }

    fetchLocalitati(localitate: string) {
        let idCountry = this.form.get('id_country')?.value;
        let idJudet = this.form.get('id_judet')?.value;

        let country = (idCountry && idCountry !== -1) ? idCountry : undefined;
        let judet = (idJudet && idJudet !== -1) ? idJudet : undefined;

        if (localitate && localitate.length >= 1) {
            this.isLocalitatiLoading = true;
            this.localitatiService.getLocalitati(judet, localitate, country).subscribe({
                next: (response: any) => {
                    this.localitati = response.data;
                    this.isLocalitatiLoading = false;
                    this.localitatiLoadComplete = true;
                    this.showLocalitatiMessage = this.localitati.length === 0;
                },
                error: (err: any) => {
                    this.isLocalitatiLoading = false;
                    this.showLocalitatiMessage = true;
                    this.localitatiLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.localitati = [];
            this.showLocalitatiMessage = true;
            this.localitatiLoadComplete = false;
        }
    }

    submitForm() {
        this.isLoading = true;

        const updatedStrada: StradaUpdate = {
            name: this.form.get('name')?.value,
            id_country: this.form.get('id_country')?.value,
            id_judet: this.form.get('id_judet')?.value,
            id_localitate: this.form.get('id_localitate')?.value,
            id: this.data.data.id,
        };
        this.straziService
            .updateStrada(updatedStrada)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
